import React, { useEffect, useState } from "react";
import {
  MessageCircle,
  ArrowRight,
  ShieldCheck,
  Brain,
  Smartphone,
  Building2,
  CreditCard,
  Zap,
  Globe2,
  CornerDownRight,
  Check,
  Rocket,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export function IntroductoryPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-[#01180f] overflow-x-hidden selection:bg-[#C0F6B7] selection:text-[#0A4730]">
      <BackgroundGlow />
      <Navbar />

      <main className="relative z-10 space-y-20 md:space-y-32 pb-24">
        <Hero />
        <TrustedSection />
        <AIAgentSection />
        <HowItWorks />
        <PayoutMethods />
        <CoverageSection />
        <BusinessSection />
        <CTA />
      </main>

      <Footer />
    </div>
  );
}

/* ---------------- NAVBAR ---------------- */

function Navbar() {
  const navigate = useNavigate();

  return (
    <nav className="relative z-20 px-6 py-6 md:py-8">
      <div className="max-w-5xl mx-auto flex justify-between items-center">

        <div className="flex items-center gap-2">
          <img
            src="/BlupayLogo.png"
            alt="BluPay"
            className="w-8 h-8 object-contain"
          />

          <h1 className="text-2xl font-serif font-semibold tracking-tight">
            BluPay
          </h1>
        </div>

        <div className="hidden md:flex gap-8 items-center text-sm font-medium text-[#0A4730]/70">
          <a href="#features" className="hover:text-[#0A4730] transition">
            Features
          </a>

          <a href="#coverage" className="hover:text-[#0A4730] transition">
            Coverage
          </a>

          <a href="#business" className="hover:text-[#0A4730] transition">
            Business
          </a>
        </div>

        <button
          className="bg-[#0A4730] text-white text-sm font-medium px-5 py-2.5 rounded-full active:scale-[0.98] transition hover:opacity-95 shadow-sm"
          onClick={() => navigate("/signup")}
        >
          Get Started
        </button>
      </div>
    </nav>
  );
}

/* ---------------- HERO ---------------- */

function Hero() {
  return (
    <section className="px-6 pt-4 md:pt-14">
      <div className="max-w-5xl mx-auto flex flex-col">

        {/* -------------------------------------------------
            PRODUCT DEMO

            Mobile:
            - Appears FIRST
            - Slight zoom-out entrance

            Desktop:
            - Appears AFTER hero copy
            - No zoom animation
        -------------------------------------------------- */}
        <div className="order-1 md:order-2">
          <ProductDemoStage />
        </div>

        {/* -------------------------------------------------
            HERO COPY

            Mobile:
            - Appears AFTER the demo

            Desktop:
            - Appears BEFORE the demo
        -------------------------------------------------- */}
        <div className="order-2 md:order-1 flex flex-col items-center text-center">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-semibold leading-[1.05] tracking-tight">
              Send money home{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0A4730] to-[#01100b]">
                via text.
              </span>
            </h1>

            <p className="mx-auto text-base md:text-lg text-[#0A4730]/70 max-w-xl leading-relaxed">
              Transfer money to and within Africa instantly through chat. No
              forms, no friction. Just smart, secure payments.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <button className="bg-[#01100b] text-white px-6 py-4 rounded-2xl flex items-center justify-center gap-2 font-medium active:scale-[0.98] transition shadow-md hover:shadow-lg w-full sm:w-auto">
                Start Chatting
              </button>

              <button className="bg-white border border-[#01100b]/10 text-[#01100b] px-6 py-4 rounded-2xl font-medium active:scale-[0.98] transition shadow-sm hover:bg-gray-50 w-full sm:w-auto">
                Explore Rates
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs font-medium text-[#01100b]/50 pt-1">
              <ShieldCheck size={16} className="text-[#01100b]" />
              Bank-level 256-bit encryption
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRODUCT DEMO STAGE ---------------- */

function ProductDemoStage() {
  return (
    <section className="relative mt-2 md:mt-20">
      <div
        className="
          relative
          mx-auto
          max-w-4xl
          min-h-[620px]
          md:min-h-[760px]
          flex
          flex-col
          items-center
          justify-center
          overflow-hidden
        "
      >
        {/* Existing brand glow */}
        <div
          className="
            absolute
            left-1/2
            top-1/2
            -translate-x-1/2
            -translate-y-1/2
            w-[280px]
            md:w-[420px]
            h-[500px]
            md:h-[650px]
            bg-[#C0F6B7]
            rounded-[5rem]
            blur-[90px]
            opacity-20
          "
        />

        {/* Very subtle atmosphere */}
        <div
          className="
            absolute
            inset-x-0
            top-1/2
            -translate-y-1/2
            h-[480px]
            md:h-[620px]
            bg-[#C0F6B7]/5
            blur-3xl
            rounded-full
            pointer-events-none
          "
        />

        <div className="relative z-10 flex flex-col items-center">
          <div className="mb-6 md:mb-9 text-center">
            <p className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-[#01100b]/40">
              See BluPay in action
            </p>
          </div>

          <PhoneDemo />

          <div className="mt-7 md:mt-10 flex items-center gap-2 text-xs text-[#01100b]/40">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C0F6B7]" />
            <span>From message to money received</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- ANIMATED PHONE DEMO ---------------- */

function PhoneDemo() {
  const messages = [
    {
      type: "user",
      text: "Send £100 to Martha in Kenya",
    },
    {
      type: "ai",
      text: "Found Martha Nderitu.\nExchange rate: 1 GBP = 130 KES\nMartha gets KES 13,000.\n\nProceed?",
    },
    {
      type: "user",
      text: "Yes, send it.",
    },
    {
      type: "ai",
      text: (
        <>
          <span className="flex items-center gap-1">
            Transfer complete!{" "}
            <Rocket size={14} className="text-[#0A4730]" />
          </span>

          Marthar received KES 13,000 instantly to her M-Pesa.
        </>
      ),
    },
  ];

  const [visible, setVisible] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible((v) => (v < messages.length ? v + 1 : 1));
    }, 2500);

    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <>
      {/* Mobile-only zoom-out animation */}
      <style>{`
        @keyframes blupayPhoneZoomOut {
          0% {
            opacity: 0;
            transform: scale(1.12);
          }

          55% {
            opacity: 1;
            transform: scale(1.035);
          }

          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (min-width: 768px) {
          .blupay-phone-entrance {
            animation: none;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .blupay-phone-entrance {
            animation: none;
          }
        }
      `}</style>

      <div className="flex justify-center relative">
        <div
          className="
            blupay-phone-entrance
            relative
            w-[260px]
            h-[540px]
            md:w-[280px]
            md:h-[580px]
            bg-[#0A4730]
            rounded-[2.5rem]
            p-2
            shadow-2xl
            border-4
            border-gray-100
          "
          style={{
            animation:
              "blupayPhoneZoomOut 900ms cubic-bezier(0.22, 1, 0.36, 1) both",
          }}
        >
          <div className="bg-gray-50 h-full w-full rounded-[2.2rem] overflow-hidden flex flex-col relative">

            {/* PHONE HEADER */}
            <div className="bg-white px-5 py-4 flex items-center gap-3 border-b border-[#0A4730]/5 shadow-sm z-10">
              <div className="w-8 h-8 rounded-full bg-[#0A4730] flex items-center justify-center text-[#C0F6B7]">
                <Brain size={16} />
              </div>

              <div>
                <p className="text-sm font-serif font-semibold text-[#0A4730]">
                  BluPay AI
                </p>

                <p className="text-[10px] text-emerald-600 font-medium">
                  Online
                </p>
              </div>
            </div>

            {/* CHAT */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto scrollbar-hidden">
              {messages.slice(0, visible).map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.type === "user"
                      ? "justify-end"
                      : "justify-start"
                  } animate-in fade-in slide-in-from-bottom-2 duration-300`}
                >
                  <div
                    className={`px-4 py-3 rounded-2xl max-w-[85%] text-sm leading-relaxed shadow-sm whitespace-pre-line ${
                      m.type === "user"
                        ? "bg-[#C0F6B7] text-[#0A4730] rounded-br-sm"
                        : "bg-white text-[#0A4730]/80 border border-[#0A4730]/5 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            {/* INPUT */}
            <div className="p-4 bg-white border-t border-[#0A4730]/5">
              <div className="bg-gray-50 rounded-full px-4 py-3 flex items-center gap-2 border border-[#0A4730]/10">
                <div className="h-4 w-4 rounded-full bg-[#0A4730]/10 animate-pulse" />
                <div className="h-1 w-24 bg-[#0A4730]/10 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------- TRUSTED BY ---------------- */

function TrustedSection() {
  return (
    <section className="px-6">
      <div className="max-w-5xl mx-auto border-y border-[#0A4730]/10 py-10 text-center">
        <p className="text-xs font-bold tracking-widest uppercase text-[#0A4730]/40 mb-6">
          Lighting up corridors across Africa
        </p>

        <div className="flex flex-wrap justify-center gap-6 md:gap-12 text-sm md:text-base font-medium text-[#0A4730]/70">
          <span className="flex items-center gap-2">
            <Globe2 size={18} /> World to Kenya
          </span>

          <span className="flex items-center gap-2">
            <Globe2 size={18} /> World to Nigeria
          </span>

          <span className="flex items-center gap-2">
            <Globe2 size={18} /> World to Ghana
          </span>

          <span className="flex items-center gap-2">
            <Globe2 size={18} /> World to Cameroon
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- AI INTELLIGENCE ---------------- */

function AIAgentSection() {
  return (
    <section className="px-6" id="features">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div className="order-2 md:order-1 bg-white p-6 md:p-8 rounded-[2rem] border border-[#0A4730]/10 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#C0F6B7]/20 rounded-bl-full" />

          <div className="space-y-4 relative z-10">
            <Chat user="Pay team bonuses: Alex £50, Sarah £75" />
            <Chat ai="Drafted 2 transfers. Total £125. Review and confirm?" />
            <Chat user="Confirm" />

            <div className="mt-4 bg-[#C0F6B7]/20 border border-[#C0F6B7] text-[#0A4730] text-xs font-semibold px-4 py-2 rounded-xl flex items-center justify-center gap-2">
              <Check size={14} strokeWidth={3} />
              Multi-recipient payout successful
            </div>
          </div>
        </div>

        <div className="order-1 md:order-2 space-y-6">
          <div className="w-12 h-12 rounded-2xl bg-[#0A4730] flex items-center justify-center text-[#C0F6B7]">
            <Brain size={24} />
          </div>

          <h2 className="text-3xl md:text-4xl font-serif font-semibold leading-tight">
            The intelligence behind every transfer.
          </h2>

          <p className="text-[#0A4730]/70 text-base leading-relaxed">
            BluPay doesn't just pass messages. Our Agent understands natural
            language, securely fetches recipient details, locks in the best
            exchange rates, and executes transfers autonomously.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- HOW IT WORKS ---------------- */

function HowItWorks() {
  return (
    <section className="px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold">
            How it works
          </h2>

          <p className="text-[#0A4730]/60 mt-3 text-sm md:text-base">
            Three steps. Zero friction.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-6">
          <Step
            icon={<MessageCircle />}
            title="1. Message"
            desc="Tell BluPay who to pay and how much in plain English."
          />

          <Step
            icon={<Brain />}
            title="2. Review"
            desc="Our agent locks the rate and confirms the recipient."
          />

          <Step
            icon={<Zap />}
            title="3. Sent"
            desc="Money arrives instantly in their local wallet or bank."
          />
        </div>
      </div>
    </section>
  );
}

/* ---------------- PAYOUT METHODS ---------------- */

function PayoutMethods() {
  return (
    <section className="px-6">
      <div className="max-w-5xl mx-auto bg-[#01100b] rounded-[2.5rem] p-8 md:p-16 text-center text-white relative overflow-hidden">
        <div className="absolute top-[-50%] left-1/2 -translate-x-1/2 w-[80%] h-[100%] bg-[#C0F6B7] opacity-10 blur-[100px] rounded-full pointer-events-none" />

        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-10 text-[#C0F6B7]">
            Every payout method. <br className="md:hidden" /> One chat.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            <DarkCard
              icon={<Smartphone />}
              title="Mobile Money"
              subtitle="M-Pesa, MTN, Airtel"
            />

            <DarkCard
              icon={<Building2 />}
              title="Bank Transfers"
              subtitle="Direct to local banks"
            />

            <DarkCard
              icon={<CreditCard />}
              title="Card Deposits"
              subtitle="Visa & Mastercard"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- COVERAGE SECTION ---------------- */

function CoverageSection() {
  return (
    <section
      id="coverage"
      className="relative w-full bg-[#01100b] text-white py-24 md:py-32 overflow-hidden border-y border-[#0A4730]"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundSize: "32px 32px",
        }}
      />

      <div className="absolute inset-0 pointer-events-none opacity-50 hidden md:block">
        <AnimatedCorridorLines />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-3 text-[#C0F6B7]/80 text-xs font-bold tracking-[0.2em] mb-6">
            <CornerDownRight size={14} strokeWidth={3} />
            <span>COVERAGE CURRENT AND UPCOMING</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-serif font-semibold leading-tight tracking-tight mb-6 text-white">
            The corridors{" "}
            <span className="text-[#C0F6B7]">already lit up.</span>
          </h2>

          <p className="text-white/60 text-lg md:text-xl max-w-2xl leading-relaxed">
            Push dollars, pounds and euros from senders in the US, UK and
            Europe, OR move money directly between African countries. Drop it
            into bank accounts, mobile money wallets and cash pickup, usually
            before the kettle boils.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12 md:gap-6">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs font-bold">
                1
              </span>

              <h3 className="font-bold tracking-widest text-sm uppercase text-[#C0F6B7]">
                Collect From
              </h3>
            </div>

            <div className="inline-block bg-[#E5B842]/10 border border-[#C0F6B7] text-[#C0F6B7] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">
              Live
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CountryPill emoji="🇺🇸" name="USA" />
              <CountryPill emoji="🇬🇧" name="UK" />
              <CountryPill emoji="🇪🇺" name="Europe" />
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded bg-white/10 text-[#C0F6B7] flex items-center justify-center text-xs font-bold">
                2
              </span>

              <h3 className="font-bold tracking-widest text-sm text-[#C0F6B7] uppercase">
                Currently Operating In
              </h3>
            </div>

            <div className="inline-block bg-[#C0F6B7]/10 border border-[#C0F6B7] text-[#C0F6B7] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">
              Live
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CountryPill emoji="🇳🇬" name="Nigeria" />
              <CountryPill emoji="🇰🇪" name="Kenya" />
              <CountryPill emoji="🇬🇭" name="Ghana" />
              <CountryPill emoji="🇨🇲" name="Cameroon" />
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded bg-white/10 text-[#C0F6B7] flex items-center justify-center text-xs font-bold">
                3
              </span>

              <h3 className="font-bold tracking-widest text-sm text-[#C0F6B7] uppercase">
                Send To
              </h3>
            </div>

            <div className="inline-block bg-transparent border border-[#C0F6B7] text-[#eee3c1] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-2">
              Coming Soon
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CountryPill emoji="🇵🇭" name="Philippines" />
              <CountryPill emoji="🇺🇬" name="Uganda" />
              <CountryPill emoji="🇿🇦" name="South Africa" />
            </div>
          </div>
        </div>

        <div className="mt-16 bg-transparent border border-[#C0F6B7] rounded-2xl p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 backdrop-blur-md">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-transparent px-3 py-1.5 rounded-full border border-[#C0F6B7]">
              <div className="w-2 h-2 rounded-full bg-[#C0F6B7] animate-pulse" />

              <span className="text-[#C0F6B7] text-xs font-bold tracking-widest uppercase">
                Intra-Africa · Live
              </span>
            </div>
          </div>

          <p className="flex-1 text-sm md:text-base text-[#eee3c1] leading-relaxed">
            Send between African countries in the same chat at the same 1–3%
            all-in rate.
          </p>

          <div className="flex -space-x-3">
            <div className="w-8 h-8 rounded-full bg-[#0A4730] border-2 border-[#04261A] flex items-center justify-center text-sm shadow-sm">
              🇰🇪
            </div>

            <div className="w-8 h-8 rounded-full bg-[#0A4730] border-2 border-[#04261A] flex items-center justify-center text-sm shadow-sm">
              🇬🇭
            </div>

            <div className="w-8 h-8 rounded-full bg-[#0A4730] border-2 border-[#04261A] flex items-center justify-center text-sm shadow-sm">
              🇨🇲
            </div>

            <div className="w-8 h-8 rounded-full bg-[#0A4730] border-2 border-[#04261A] flex items-center justify-center text-sm shadow-sm">
              🇲🇺
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CountryPill({ emoji, name, active }) {
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
        active
          ? "bg-white/10 border-white/20 hover:bg-white/15"
          : "bg-transparent border-white/5 hover:border-white/10 opacity-70"
      }`}
    >
      <span className="text-lg leading-none">{emoji}</span>
      <span className="text-sm font-medium text-white">{name}</span>
    </div>
  );
}

function AnimatedCorridorLines() {
  return (
    <>
      <style>{`
        @keyframes dash {
          0% {
            stroke-dashoffset: 1000;
          }

          100% {
            stroke-dashoffset: 0;
          }
        }

        .animated-path {
          stroke-dasharray: 1000;
          animation: dash 8s linear infinite;
        }

        .animated-path-reverse {
          stroke-dasharray: 1000;
          animation: dash 10s linear infinite reverse;
        }
      `}</style>

      <svg
        className="w-full h-full"
        viewBox="0 0 1000 400"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient
            id="line-glow"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#04261A" />
            <stop offset="50%" stopColor="#C0F6B7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#04261A" />
          </linearGradient>

          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />

            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d="M 150 250 C 300 250, 400 150, 500 150 C 600 150, 700 250, 850 250"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="2"
        />

        <path
          d="M 150 150 C 350 100, 450 300, 500 300 C 650 300, 750 100, 850 150"
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="2"
        />

        <path
          className="animated-path"
          d="M 150 250 C 300 250, 400 150, 500 150 C 600 150, 700 250, 850 250"
          fill="none"
          stroke="url(#line-glow)"
          strokeWidth="2"
          filter="url(#glow)"
        />

        <path
          className="animated-path-reverse"
          d="M 150 150 C 350 100, 450 300, 500 300 C 650 300, 750 100, 850 150"
          fill="none"
          stroke="url(#line-glow)"
          strokeWidth="1.5"
          filter="url(#glow)"
        />
      </svg>
    </>
  );
}

/* ---------------- BUSINESS ---------------- */

function BusinessSection() {
  return (
    <section className="px-6" id="business">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-12 items-center bg-white p-8 md:p-12 rounded-[2.5rem] border border-[#0A4730]/10 shadow-sm">
        <div className="flex-1 space-y-5">
          <div className="inline-block bg-[#0A4730]/5 text-[#0A4730] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            BluPay for Business
          </div>

          <h2 className="text-3xl md:text-4xl font-serif font-semibold leading-tight">
            Scale your team payouts.
          </h2>

          <p className="text-[#0A4730]/70 leading-relaxed text-base">
            Managing remote contractors across borders? Ditch the complicated
            payroll software. Just message BluPay to run your monthly payroll
            in seconds.
          </p>

          <button className="text-[#0A4730] font-semibold flex items-center gap-2 hover:gap-3 transition-all pt-2">
            Explore Business API <ArrowRight size={16} />
          </button>
        </div>

        <div className="flex-1 w-full flex justify-center md:justify-end">
          <div className="bg-gray-50 border border-[#0A4730]/10 p-6 rounded-[2rem] w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#0A4730]/5">
              <div className="w-10 h-10 rounded-full bg-[#0A4730] flex items-center justify-center text-white font-bold">
                BP
              </div>

              <div>
                <p className="font-semibold text-sm">
                  Payroll Batch #402
                </p>

                <p className="text-xs text-[#0A4730]/50">
                  12 Recipients • £3,450.00
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-[#0A4730]/70">
                  John Doe (KES)
                </span>

                <span className="font-medium">£450.00</span>
              </div>

              <div className="flex justify-between items-center text-sm">
                <span className="text-[#0A4730]/70">
                  Amara Okafor (NGN)
                </span>

                <span className="font-medium">£800.00</span>
              </div>

              <div className="flex justify-between items-center text-sm">
                <span className="text-[#0A4730]/70">
                  ...and 10 others
                </span>

                <span className="font-medium">£2,200.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CTA ---------------- */

function CTA() {
  const navigate = useNavigate();

  return (
    <section className="px-6 text-center pt-10">
      <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6">
        Ready to simplify your transfers?
      </h2>

      <button
        className="mt-2 bg-[#0A4730] text-white px-8 py-4 rounded-2xl font-medium active:scale-[0.98] transition shadow-md hover:shadow-lg text-lg"
        onClick={() => navigate("/signup")}
      >
        Create free account
      </button>

      <p className="mt-4 text-xs text-[#0A4730]/50">
        No hidden fees. Takes 2 minutes.
      </p>
    </section>
  );
}

/* ---------------- HELPERS ---------------- */

function Chat({ user, ai }) {
  const isUser = !!user;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`px-4 py-3 rounded-2xl text-sm md:text-base leading-relaxed max-w-[90%] shadow-sm ${
          isUser
            ? "bg-[#C0F6B7] text-[#0A4730] rounded-br-sm"
            : "bg-gray-50 border border-[#0A4730]/10 text-[#0A4730]/80 rounded-bl-sm"
        }`}
      >
        {user || ai}
      </div>
    </div>
  );
}

function Step({ icon, title, desc }) {
  return (
    <div className="p-6 md:p-8 bg-white border border-[#0A4730]/5 rounded-[2rem] shadow-sm hover:shadow-md transition text-left">
      <div className="w-10 h-10 rounded-xl bg-[#C0F6B7]/30 flex items-center justify-center text-[#0A4730] mb-5">
        {icon}
      </div>

      <h3 className="font-serif font-semibold text-lg mb-2">
        {title}
      </h3>

      <p className="text-sm text-[#0A4730]/70 leading-relaxed">
        {desc}
      </p>
    </div>
  );
}

function DarkCard({ icon, title, subtitle }) {
  return (
    <div className="p-6 bg-[#B9F2A1] border-[#B9F2A1] rounded-[2rem] backdrop-blur-sm text-left hover:bg-white transition cursor-default">
      <div className="text-[#01100b] mb-4">{icon}</div>

      <h3 className="font-semibold text-[#01100b] text-lg">
        {title}
      </h3>

      <p className="text-[#01100b] text-sm mt-1">
        {subtitle}
      </p>
    </div>
  );
}

function BackgroundGlow() {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      <div className="absolute w-[600px] h-[600px] bg-[#C0F6B7] opacity-[0.15] blur-[120px] rounded-full -top-40 -left-40" />

      <div className="absolute w-[500px] h-[500px] bg-[#000a06] opacity-[0.03] blur-[100px] rounded-full right-0 top-1/3" />
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#0A4730]/10 bg-transparent py-8 relative z-10">
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 text-[#0A4730]">
          <img
            src="/BlupayLogo.png"
            alt="BluPay"
            className="w-8 h-8 object-contain"
          />

          <span className="font-serif font-semibold">
            BluPay
          </span>
        </div>

        <div className="text-xs text-[#0A4730]/50">
          © {new Date().getFullYear()} BluPay Technologies. All rights reserved.
        </div>
      </div>
    </footer>
  );
}